import { Textarea } from '@/components/ui/textarea';
import { negativeFeedbackOptions } from '@/config/chatConfig';
import { useChatApi } from '@/hooks/useChatApi';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { translateConfigValue } from '@/utils/translateConfigValue';
import { SendIcon } from 'lucide-react';
import { memo, useCallback, useEffect, useRef } from 'react';
import ChatWatermark from '../ChatWatermark';
import { Button } from '../ui/button';

function MessageInput({ suggestions }: { suggestions: string[] }) {
  const { showNegativeFeedbackMessageId, setShowNegativeFeedbackMessageId } = useChatStore();

  const {
    inputValue,
    handleInputChange,
    handleKeyDown,
    sendMessage,
    isLoading,
    sendSuggestion,
    sendFeedback,
  } = useChatApi();

  const { config } = useConfigStore();

  const generalSpecificVariant = config.ui?.generalSpecificVariant;
  const language = config.language ?? 'de';
  const placeholder = translateConfigValue(config?.ui?.input?.placeholder, language);
  const whatWentWrong = translateConfigValue(config?.ui?.feedback?.whatWentWrong, language);

  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isLoading) {
      ref.current?.focus();
    }
  }, [isLoading]);

  const handleSuggestionClick = (suggestion: string) => {
    sendSuggestion(suggestion);
  };

  const handleNegativeFeedbackClick = useCallback(
    (option: string) => {
      if (!showNegativeFeedbackMessageId) return;

      sendFeedback({
        messageId: showNegativeFeedbackMessageId,
        content: option,
        type: 'negative',
      });
      setShowNegativeFeedbackMessageId(null);
    },
    [showNegativeFeedbackMessageId, sendFeedback, setShowNegativeFeedbackMessageId]
  );

  return (
    <div
      className={cn(
        'mx-[-16px] flex w-full max-w-[780px] flex-col items-center gap-2 border-t border-gray-200 bg-white px-[16px] py-4 md:mx-[-20px] md:px-[20px] lg:mx-[-32px] lg:px-[32px]',
        {
          'rounded-b-[10px] py-2 md:mx-[-16px] md:px-[16px] lg:mx-[-16px] lg:px-[16px]':
            generalSpecificVariant === 'velio',
        }
      )}
    >
      {showNegativeFeedbackMessageId ? (
        <div className="flex w-full flex-col gap-[12px] font-[500]">
          <p className="text-sm text-widget-text-secondary">{whatWentWrong}</p>
          <div className="flex w-full max-w-full flex-row flex-wrap gap-2">
            {negativeFeedbackOptions[language].map(option => (
              <Button
                key={option}
                variant={'suggestion'}
                onClick={() => handleNegativeFeedbackClick(option)}
                className="inline-block h-auto max-h-none max-w-full text-wrap text-left"
              >
                {option}
              </Button>
            ))}
          </div>
        </div>
      ) : (
        suggestions.length > 0 && (
          <div
            className={cn('flex w-full max-w-full flex-row flex-wrap gap-2', {
              'flex-row-reverse': generalSpecificVariant === 'velio',
            })}
          >
            {suggestions.slice(0, 2)?.map(suggestion => (
              <Button
                key={suggestion}
                variant={generalSpecificVariant === 'velio' ? 'suggestionVelio' : 'suggestion'}
                onClick={() => handleSuggestionClick(suggestion)}
                className="inline-block h-auto max-h-none max-w-full text-wrap text-left"
              >
                {suggestion}
              </Button>
            ))}
          </div>
        )
      )}
      <div className="flex w-full flex-row items-center gap-2">
        <Textarea
          ref={ref}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 resize-none"
          disabled={isLoading}
        />
        {config?.ui?.input?.sendButtonVariant === 'primary' && (
          <Button
            variant={generalSpecificVariant === 'velio' ? 'outlineVelio' : 'outline'}
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim()}
            className="size-[44px] shrink-0"
          >
            <SendIcon className="h-5 w-5" />
          </Button>
        )}
        {config?.ui?.input?.sendButtonVariant === 'black' && (
          <Button
            variant={'outline'}
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim()}
            className="size-[44px] rounded-full bg-black transition-colors duration-200 hover:!bg-black/80 active:!bg-black/60"
          >
            <svg
              width="22"
              height="18"
              viewBox="0 0 22 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M22 8.99999L6.76923 16.3279L6.76923 1.67208L22 8.99999Z" fill="white" />
              <rect
                x="6.76904"
                y="7.30756"
                width="3.38462"
                height="6.76923"
                transform="rotate(90 6.76904 7.30756)"
                fill="white"
              />
            </svg>
          </Button>
        )}
      </div>
      <ChatWatermark />
    </div>
  );
}

export default memo(MessageInput);
